import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { Case } from 'src/app/core/models/case';

import { Faq } from 'src/app/core/models/faq';
import { Filter } from 'src/app/core/services/base/database.service';
import { CaseService } from 'src/app/core/services/case.service';
import { FaqService } from 'src/app/core/services/faq.service';
import { Breadcrumb } from 'src/app/shared/components/simple/breadcrumb/breadcrumb.component';
import { TextFilterConfig } from 'src/app/shared/components/smart/table/table.component';
import { SignedContractService } from 'src/app/core/services/signed_contract.service';
import { ContractService } from 'src/app/core/services/contract.service';
import { UserService } from 'src/app/core/services/user.service';
import { ClaimsService } from 'src/app/core/services/claims.service';

export type CategoryListItem = {
  label: string;
  icon: string;
};

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
})
export class FaqComponent implements OnInit {
  faqs: Faq[] = [];
  categoryList: CategoryListItem[] = [
    {
      label: 'News',
      icon: 'newspaper',
    },
    {
      label: 'Profile',
      icon: 'person_outline',
    },
    {
      label: 'Clients',
      icon: 'group',
    },
    {
      label: 'Security',
      icon: 'security',
    },
    {
      label: 'Notifications',
      icon: 'notifications_outline',
    },
    {
      label: 'Pricing',
      icon: 'paid_outline',
    },
    {
      label: 'Claims',
      icon: 'campaign_outline',
    },
  ];

  selectedCategory?: CategoryListItem;
  breadCrumbItems: Breadcrumb[] = [
    { label: 'Home', url: '/client/home' },
    { label: 'FAQ' },
    { label: 'Cases' },
  ];

  faqsToShow: Record<string, Faq[]> = {};
  cases: Case[] = [];
  loading = false;
  searchValue?: string;
  caseId?: string;
  searchByCaseFromUrl = false;
  showCases = true;
  selectedCaseIndex = -1;
  private claimantStatusMap: Record<
    string,
    { isClaimant: boolean; isBlfClaimant: boolean }
  > = {};
  isBlfClaimant = false;
  isRegularClaimant = false;

  constructor(
    private faqService: FaqService,
    private caseService: CaseService,
    private signedContractService: SignedContractService,
    private userService: UserService,
    private claimService: ClaimsService
  ) {}

  ngOnInit() {
    this.loadCases();
  }

  async loadCases() {
    this.loading = true;
    try {
      this.cases = (await this.caseService.getAll(2000, 0)) as Case[];
      const user = await this.userService.getCurrentUser();
      if (user?.id) {
        this.claimantStatusMap =
          await this.claimService.buildClaimantStatusMap(user.id);
        this.updateClaimantFlags();
      } else {
        this.claimantStatusMap = {};
        this.isBlfClaimant = false;
        this.isRegularClaimant = false;
      }
    } finally {
      this.loading = false;
    }
  }

  async getFaqs() {
    this.loading = true;
    this.showCases = false;
    try {
      const filters: Filter[] = [];
      if (this.caseId) {
        filters.push({
          column: 'case_id',
          operator: 'eq',
          value: this.caseId,
          visible: true,
        });
      }
      let textFilter: TextFilterConfig | undefined;
      if (this.searchValue) {
        textFilter = {
          column: 'search_text',
          value: this.searchValue!,
          options: {
            type: 'phrase',
            config: 'simple',
          },
        };
      }

      if (!textFilter && !this.caseId) {
        this.clearFAQs();
        return;
      }

      const faqs = (
        await firstValueFrom(
          this.faqService.list(0, 100, filters, [], textFilter)
        )
      ).data;

      const faqsGroupedByCategory = faqs.reduce((acc, faq) => {
        const category: string =
          faq?.metadata?.category?.toLowerCase() || 'general';
        acc[category] = [...(acc[category] || []), faq];
        return acc;
      }, {} as Record<string, Faq[]>);


      const canShowLegalPackCategory: boolean = await this.canShowLegalPackSignatureFaq();
      this.faqsToShow = this.filterFaqsByAudience(faqsGroupedByCategory, canShowLegalPackCategory); // new filter method

    } finally {
      this.loading = false;
    }
  }

  selectCase(caseId: string) {
    console.log('selectCase', caseId);
    this.caseId = caseId;
    this.updateClaimantFlags();
    this.selectedCaseIndex = this.cases.findIndex((c) => c.id === caseId);
    this.getFaqs();

    const existingIndex = this.breadCrumbItems.findIndex((item) =>
      item.url?.startsWith('/client/faq/')
    );
    if (existingIndex !== -1) {
      this.breadCrumbItems.splice(existingIndex, 1);
    }

    const selectedCase = this.cases.find((c) => c.id === caseId);
    if (selectedCase) {
      const caseUrl = '/client/faq/' + this.caseId;
      this.breadCrumbItems.push({
        label: selectedCase.summary,
        url: caseUrl,
      });
    }
  }

  searchChange(search: string) {
    console.log('searchChange', search);
    this.searchValue = search;
    this.getFaqs();
  }

  clearFAQs() {
    this.showCases = true;
    this.selectedCaseIndex = -1;
    this.faqsToShow = {};
    this.caseId = undefined;
    this.searchValue = undefined;
    this.isBlfClaimant = false;
    this.isRegularClaimant = false;
    this.breadCrumbItems = [
      { label: 'Home', url: '/client/home' },
      { label: 'FAQ' },
      { label: 'Cases' },
    ];
  }

  private async canShowLegalPackSignatureFaq(): Promise<boolean> {

    let isLegalPackUser: boolean  = false;
    const currentCaseId = this.caseId;

    const userLogged: any = await this.userService.getCurrentUser();

    if (!userLogged?.id) {
      return false;
    }

    const contractSignedData: Array<any> = await this.signedContractService.getContractSignedByUser(userLogged.id);

    if (!contractSignedData || contractSignedData.length === 0) {
      return false;
    }

    const claimIdContractSigned: string = contractSignedData[0]?.claim_id;

    if(!claimIdContractSigned){
      return false;
    }

    const getClaimsWithCase: any = await this.claimService.getClaimWithCase(claimIdContractSigned);

     if (!getClaimsWithCase?.case_id){
      return false;
    }

    const isCaseValid: boolean = getClaimsWithCase.case_id === currentCaseId ? true : false;
    isLegalPackUser = isCaseValid;

    return isLegalPackUser;
  }

  private filterFaqsByAudience(faqsByCategory: Record<string, Faq[]>, canShowLegalPacksItems: boolean): Record<string, Faq[]> {
    const canShowLegalPackSignature = canShowLegalPacksItems;

    return Object.entries(faqsByCategory).reduce(
      (acc, [category, faqs]) => {
        const filteredFaqs = faqs.filter((faq) => {
          const audience = faq?.audience?.type_audience;

          if (audience === 'legal_pack_signature') {
            return canShowLegalPackSignature;
          }

          if (audience === 'public') {
            return true;
          }

          if (audience === 'blf_claimants') {
            return this.isBlfClaimant;
          }

          if (audience === 'claimants') {
            return this.isRegularClaimant;
          }

          return true;
        });

        if (filteredFaqs.length > 0) {
          acc[category] = filteredFaqs;
        }

        return acc;
      },
      {} as Record<string, Faq[]>
    );
  }

  private updateClaimantFlags(): void {
    const status = this.caseId ? this.claimantStatusMap[this.caseId] : undefined;
    this.isBlfClaimant = status?.isBlfClaimant ?? false;
    this.isRegularClaimant = (status?.isClaimant && !status.isBlfClaimant) ?? false;
  }

}
